<template>
    <v-container v-if="loading">
        <v-layout align-center style="min-height: 800px">
            <v-spacer/>
            <v-progress-circular indeterminate/>
            <v-spacer/>
        </v-layout>
    </v-container>
    <v-container grid-list-md class="pa-0" v-else>
        <div v-if="printing" class="pa-5 print-loading-overlay">
            <v-progress-circular indeterminate/>
            Skriver ut merkelapper. Ikke forlat siden før utskrift er ferdig.
        </div>
        <v-layout wrap>
            <v-flex md7 sm12>
                <v-card>
                    <v-toolbar flat dense color="blue-grey" dark>
                        <span class="pl-3 subheading">Grunnleggende informasjon</span>
                    </v-toolbar>
                    <v-layout wrap class="pt-3 pb-2">
                        <v-flex xs12 md6 lg6>
                            <v-card-text class="py-0">
                                <v-text-field v-model="data.brand" label="Dekkmerke" class="input-capitalize"
                                              hide-details/>
                            </v-card-text>
                        </v-flex>
                        <v-flex xs12 md6 lg6>
                            <v-card-text class="py-0">
                                <v-layout class="mb-2">
                                    <v-flex>
                                        <v-text-field v-model="data.size" label="Dimensjoner" v-mask="'###/##-##'"
                                                      return-masked-value hide-details/>
                                    </v-flex>
                                    <v-flex>
                                        <v-text-field v-model="data.size_2" label="Dimensjoner bak" v-mask="'###/##-##'"
                                                      return-masked-value placeholder="Hvis ulik" hide-details/>
                                    </v-flex>
                                </v-layout>

                                <v-layout v-if="hasOriginalSize">
                                    <v-flex class="pt-0">
                                        <v-text-field v-model="data.size_original" label="Originaldimensjoner"
                                                      v-mask="'###/##-##'"
                                                      return-masked-value hide-details clearable/>
                                    </v-flex>
                                    <v-flex class="pt-0">
                                        <v-text-field v-model="data.size_original_2" label="Orig.dim. bak"
                                                      v-mask="'###/##-##'"
                                                      return-masked-value placeholder="Hvis ulik" hide-detail
                                                      clearable/>
                                    </v-flex>
                                </v-layout>
                                <v-btn color="info" outlined small block @click="showOriginalSizeFields" v-else>
                                    <v-icon left>add</v-icon>
                                    Ulike originaldimensjoner
                                </v-btn>
                            </v-card-text>
                        </v-flex>
                    </v-layout>

                    <v-divider/>
                    <v-layout wrap class="pt-2">

                        <v-flex xs12 md6 lg8 class="pb-2">
                            <v-card-text class="py-0">
                                <v-text-field label="Kilometer" v-model="data.km" type="number" min="0" step="1"
                                              placeholder="Ukjent" hide-details/>
                            </v-card-text>
                        </v-flex>
                        <v-flex xs12 md6 lg4>
                            <v-card-text class="py-0">
                                <v-text-field v-model="tireCount" type="number" step="1" min="0"
                                              label="Antall dekk" hide-details/>
                            </v-card-text>
                        </v-flex>

                        <v-flex md6 lg4>
                            <v-subheader>Dekktype</v-subheader>
                            <v-card-text class="pt-0">
                                <v-radio-group v-model="data.type" class="pt-0">
                                    <v-radio :value="type.value" :label="type.text" v-for="type in tireTypeItems"
                                             :key="type.text"/>
                                </v-radio-group>
                            </v-card-text>
                        </v-flex>
                        <v-flex md6 lg4>
                            <v-subheader>Felgtype</v-subheader>
                            <v-card-text class="pt-0 pb-0">
                                <v-radio-group v-model="data.rims" class="pt-0 pb-0">
                                    <v-radio value="A" label="Lettmetall"/>
                                    <v-radio value="S" label="Stål"/>
                                    <v-radio :value="null" label="Ingen"/>
                                </v-radio-group>
                            </v-card-text>
                        </v-flex>
                        <v-flex md6 lg4>
                            <v-subheader>Har sensor</v-subheader>
                            <v-card-text class="pt-0">
                                <v-radio-group v-model="hasSensor" class="pt-0">
                                    <v-radio :value="true" label="Ja"/>
                                    <v-radio :value="false" label="Nei"/>
                                </v-radio-group>
                            </v-card-text>
                        </v-flex>

                        <v-flex xs6 md4>
                            <v-card-text class="pt-0">
                                <v-text-field persistent-hint v-model="data.load_index" label="Lasteindeks"
                                              hint="Verdi: 0 - 199" v-mask="'###'"/>
                            </v-card-text>
                        </v-flex>
                        <v-flex xs6 md4>
                            <v-card-text class="pt-0">
                                <v-text-field persistent-hint v-model="data.speed_rating" label="Hastighetsindeks"
                                              hint="Verdi: B - Z" v-mask="'A'"/>
                            </v-card-text>
                        </v-flex>
                        <v-flex md4>
                            <v-card-text class="pt-0">
                                <v-text-field hide-details v-model="data.location" v-mask="locationMask"
                                              placeholder="H0-00-00-0"
                                              return-masked-value label="Lokasjon"/>
                            </v-card-text>
                        </v-flex>
                    </v-layout>
                </v-card>
                <v-layout wrap class="pt-2">
                    <v-flex md12>
                        <v-subheader>Notat</v-subheader>
                        <v-textarea solo v-model="data.note" label="Utvidet notat" hide-details/>
                    </v-flex>

                    <v-flex md12 lg6>
                        <v-text-field class="mt-3" filled label="Utført av (signatur)" v-model="data.signature"
                                      hint="Signatur av den som utførte dekkskift." persistent-hint></v-text-field>
                    </v-flex>
                    <v-flex md12 lg6>
                        <v-text-field class="mt-3" filled label="Vasket av (signatur)" v-model="data.cleaned_by"
                                      hint="Signatur av den som vasket dekkene." persistent-hint></v-text-field>
                    </v-flex>

                    <v-flex md12 lg6 class="pr-2">
                        <v-subheader>Lagret fra</v-subheader>
                        <v-date-picker :first-day-of-week="1" locale="nb" v-model="data.in_at" full-width/>
                    </v-flex>
                    <v-flex md12 lg6 class="pl-2">
                        <v-subheader>Hentet ut</v-subheader>
                        <v-date-picker :first-day-of-week="1" locale="nb" v-model="data.out_at" full-width
                                       :color="!data.out_at ? 'grey lighten-1' : ''"/>
                        <v-btn color="red" text small block v-if="data.out_at" @click="data.out_at = null">
                            <v-icon small class="mr-2">event</v-icon>
                            Fjern dato for uthenting
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-flex md5 sm12>
                <v-card>
                    <v-toolbar flat dense color="blue-grey" dark>
                        <span class="subheading">Dekk</span>
                        <v-spacer/>
                        <v-btn text @click="printLabels(null)"
                               :disabled="!$root.printer && (!this.$root.settings.has_zpl)"
                               title="Skriv ut på Zebra-printer">
                            <v-icon left>vertical_split</v-icon>
                            Lag merkelapper
                        </v-btn>
                    </v-toolbar>
                    <v-divider/>

                    <div v-for="(tire, index) in data.tires">
                        <v-card-text>
                            <tire-storage-tire-form :data="tire" @print="printLabels([tire])" :tab-index="index+20"/>
                        </v-card-text>
                        <v-divider/>
                    </div>
                </v-card>
            </v-flex>
            <v-flex md6>
                <v-btn color="green" class="my-0" dark @click.native="save">
                    <v-icon left>save</v-icon>
                    Lagre
                </v-btn>
            </v-flex>
            <v-flex md6 class="text-sm-right">
                <v-btn color="red" class="my-0" dark @click.native="remove">
                    <v-icon left>delete</v-icon>
                    Slett dekklagring
                </v-btn>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import TireStorageTireForm from "./TireStorageTireForm";
import {capitalize, getTireTypes} from "../../helpers";

const newTire = (name, type) => {
    return {
        id: null,
        name: name,
        tread_depth: null,
        state: null,
        has_sensor: false,
        is_sensor_working: false
    }
};

let newStorage = function () {
    return {
        id: null,
        type: 'S',
        note: '',
        rims: 'A',
        in_at: moment().format('YYYY-MM-DD'),
        tires: [],
        size: null,
        size_2: null,
        size_original: null,
        size_original_2: null,
        signature: null,
        cleaned_by: null
    };
};

export default {
    components: {TireStorageTireForm},
    props: {
        carId: Number,
        storage: {}, // Optional. Used if copying existing.
        storageId: [Number, null]
    },
    data() {
        return {
            loading: false,
            data: newStorage(),
            hasOriginalSize: false,
            printing: false
        }
    },

    created() {
        this.loadCar();

        if (this.storageId) this.loadData();
        else if (this.storage) {
            // Clean up copied storage data.
            this.data = {
                ...this.storage,
                id: null,
                parent_id: this.storage.id,
                km: null,
                note: null,
                in_at: moment().format('YYYY-MM-DD'),
                out_at: null,
                third_party: null,
                signature: null,
                cleaned_by: null,
                tires: this.storage.tires.map(t => {
                    return {
                        ...t,
                        id: null, // Nullify ID
                        tread_depth: null,
                        state: null,
                        is_sensor_working: false
                    }
                })
            };
        } else {
            this.tireCount = 4;
        }
    },

    methods: {
        loadData() {
            this.loading = true;
            this.$http.get(`tire-storages/${this.storageId}`).then(response => {
                this.data = response.data.data;
                this.hasOriginalSize = !!(this.data.size_original || this.data.size_original_2);
            }).finally(() => {
                this.loading = false;
            })
        },

        loadCar() {
            this.$http.get(`cars/${this.carId}`).then(response => {
                this.car = response.data.data;
                if (!this.data.size_original) this.data.size_original = this.car.size_original;
                if (!this.data.size_original_2) this.data.size_original_2 = this.car.size_original_2;
                if (!this.data.load_index) this.data.load_index = this.car.load_index;
                if (!this.data.speed_rating) this.data.speed_rating = this.car.speed_rating;
            });
        },

        loadLastLocation() {
            this.$http.get(`cars/${this.carId}/location`).then(response => {
                this.data.location = response.data.data.location;
            });
        },

        showOriginalSizeFields() {
            this.hasOriginalSize = true;
        },

        save() {
            this.loading = true;

            let rims = this.data.rims;
            if (rims !== 'S' && rims !== 'A') rims = null;

            let location = this.data.location || '';
            let data = {
                ...this.data,
                brand: capitalize(this.data.brand),
                car_id: this.carId,
                location: location.replace(/-$/, ''),
                rims: rims
            };

            let request;
            if (this.storageId) request = this.$http.put(`tire-storages/${this.storageId}`, data);
            else request = this.$http.post(`tire-storages`, data);

            request.then(response => {
                this.$root.addMessage('Lagring vellykket.', 'green');

                if (response.data.data.out_at) {
                    return this.$router.push({
                        name: 'CarShow',
                        params: {
                            id: this.carId
                        }
                    });
                } else {
                    this.$emit('save', response.data.data);
                }
            }).finally(() => {
                this.loading = false;
            })
        },

        remove() {
            let ok = confirm('Er du sikker på at du vil slette denne dekklagringen?');
            if (!ok) return;

            this.loading = true;

            this.$http.delete(`tire-storages/${this.storageId}`).then(response => {
                this.$root.addMessage('Sletting vellykket.', 'green');
                return this.$router.push({
                    name: 'CarShow',
                    params: {
                        id: this.carId
                    }
                });
            }).catch(() => {
                this.$root.addMessage('Kunne ikke slette dekklagring.', 'red');
            }).finally(() => {
                this.loading = false;
            });
        },

        printLabels(printTires = null) {
            if (this.$root.settings && this.$root.settings.has_zpl) {
                return this.printZpl(printTires);
            } else {
                return this.printBrowserPrint(printTires);
            }
        },

        printZpl(printTires = null) {
            this.printing = true;

            let data = null;
            if (printTires) {
                data = {tires: printTires.map(it => it.id)};
            }
            this.$http.post(`tire-storages/${this.storageId}/print/zpl`, data).then(response => {
                this.$root.addMessage(response.data.message, 'green');
            }).catch(error => {
                this.$root.addMessage(error.response.data.message, 'red');
            }).finally(() => {
                this.printing = false;
            });
        },

        printBrowserPrint(printTires = null) {
            //let ok = confirm('Vil du skrive ut merkelapper for dekk på Zebra-printeren?');
            //if (!ok) return;
            let getPrinterPromise = axios('https://localhost:9101/available');
            // DEBUG:
            // let getPrinterPromise = new Promise((resolve, reject) => {
            //     setTimeout(() => {
            //         resolve({data: {printer: 'DEBUG'}})
            //     }, 500);
            // });

            this.printing = true;
            getPrinterPromise.then(async response => {
                console.log('Got printer info');
                let printer = response.data.printer[0];

                // If no tires specified, print all:
                if (!printTires) {
                    printTires = this.data.tires.filter(tire => !!tire.id);
                }
                console.log('Printing tires:', printTires);

                // Print synchronously.
                for (let i = 0; i < printTires.length; i++) {
                    let tire = printTires[i];
                    console.log('Printing', i);
                    let printPromise = axios.post('https://localhost:9101/write', {
                        device: printer,
                        url: `${window.location.protocol}//${window.location.host}/print/label/${this.storageId}/${tire.id}.jpg`
                    }).then(response => new Promise(resolve => {
                        console.log('Printed tire', i);
                        setTimeout(() => resolve(response), 1000);
                    })).catch(error => {
                        alert('Feil fra printerdriver. Send mail til olav@promotek.no hvis dette skjer under utskrift.')
                        console.log('Error printing', i, error);
                    });
                    // DEBUG:
                    // let printPromise = new Promise((resolve, reject) => {
                    //     setTimeout(() => {
                    //         console.log('Printed', i);
                    //         resolve({data: {printer: 'DEBUG'}})
                    //     }, 500);
                    // });
                    await printPromise;
                }
            }).catch(error => {
                console.log(error);
                alert('Klarte ikke å koble til Zebra-printeren. Prøv å laste inn siden på nytt, og skriv ut igjen.');
            }).finally(() => {
                this.printing = false;
            })
        }
    },

    watch: {
        storageId(val) {
            this.loadCar();
            if (val) this.loadData();
            else {
                this.data = newStorage();
                this.tireCount = 4;
                this.loadLastLocation();
            }
        },
        '$route.query.new'() {
            this.data = newStorage();
            this.tireCount = 4;
            this.loadLastLocation();
        }
    },

    computed: {
        locationMask() {
            return this.$root.settings.location_mask || 'A#-##-##-#';
        },
        tireCount: {
            get() {
                return this.data.tires.length;
            },
            set(newCount) {
                newCount = newCount | 0;

                if (newCount > this.tireCount) {
                    for (let i = this.tireCount; i < newCount; i++) {
                        let name = 'Reserve';
                        if (i === 0) name = 'Venstre foran';
                        else if (i === 1) name = 'Høyre foran';
                        else if (i === 2) name = 'Venstre bak';
                        else if (i === 3) name = 'Høyre bak';

                        this.data.tires.push(newTire(name, this.data.type));
                    }
                } else {
                    this.data.tires = this.data.tires.slice(0, newCount)
                }
            }
        },
        hasSensor: {
            get() {
                const tireCount = this.data.tires.length;
                if (tireCount === 0) return null;

                const sensorCount = this.data.tires.filter(it => it.has_sensor).length;

                if (tireCount === sensorCount) return true;
                else if (sensorCount === 0) return false;
                return null;
            },
            set(val) {
                this.data.tires.forEach(it => it.has_sensor = !!val);
            }
        },

        tireTypeItems() {
            return getTireTypes();
        }
    }
}
</script>


<style>
.print-loading-overlay {
    z-index: 1000;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(255, 255, 255, 0.8);
    text-align: center;
}
</style>
